import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  getPermissionScope,
  PermissionKey,
  PermissionScope,
  type PermissionKeyEnum,
  type PermissionScopeEnum,
} from "@fitness-app/data-models";
import { AppAccessType } from "@fitness-app/data-models/entities/Automation";
import { TraineeStatus, type TraineeWithAssignedTrainer } from "@fitness-app/data-models/entities/Trainee";
import { fetchAllPostgrestRows } from "@fitness-app/utils/src/helpers/fetchAllPostgrestRows";

import { type AsyncThunkCreator } from "../../../index";
import { getUserClaims, getUserPermissions } from "../../user/selectors";
import { TRAINEES_REDUCER_NAME } from "../types";

type Payload = {
  /** Also return limited-access clients (default: full access only, TRE-801). */
  includeLimited?: boolean;
  /**
   * Permission whose scope narrows the list. Defaults to `clients.read` — pass the key the
   * consuming action is actually authorized on, so the picker is a mirror of the backend gate
   * and the user cannot select someone the use case will reject (TRE-2148): mass chat sending
   * runs on `chat.write`, campaigns on `campaigns.manage`.
   */
  scopeKey?: PermissionKeyEnum;
} | void;

/** trainee."productId" points at product_client (per-purchase row) — the catalog product hangs off it. */
export type ActiveTraineeListItem = TraineeWithAssignedTrainer & {
  type: string;
  productClient: { product: { id: string; name: string } | null } | null;
};

const SELECT_COLUMNS =
  "id, userId, firstName, lastName, email, type, createdAt, assignedTrainerId, assignedTrainer:assignedTrainerId(id, firstName, lastName, userId, email), productClient:productId(product:productId(id, name))";

/** `null` = no narrowing (whole tenant), `[]` = nobody. */
export type TrainerIdScope = string[] | null;

/**
 * Turns a permission scope into the `assignedTrainerId` values the caller may see, mirroring
 * `TraineePermissionScopeResolver` on the backend so the picker cannot offer a client the use
 * case will reject.
 *
 * `fetchSubordinateIds` returns `null` when the lookup failed — that falls back to the head
 * coach's own clients rather than to the whole tenant.
 */
export const resolveTrainerIdScope = async (
  scope: PermissionScopeEnum,
  memberId: string | undefined,
  fetchSubordinateIds: (headCoachId: string) => Promise<string[] | null>,
): Promise<TrainerIdScope> => {
  if (scope === PermissionScope.ALL) {
    return null;
  }

  if (scope === PermissionScope.NONE || !memberId) {
    return [];
  }

  if (scope === PermissionScope.ASSIGNED) {
    return [memberId];
  }

  const subordinateIds = await fetchSubordinateIds(memberId);

  return subordinateIds ? [memberId, ...subordinateIds] : [memberId];
};

export const fetchAllActiveTrainees = createAsyncThunk<
  {
    list: ActiveTraineeListItem[];
    count: number;
  },
  Payload,
  AsyncThunkCreator<string>
>(`${TRAINEES_REDUCER_NAME}/fetchAllActiveTrainees`, async (payload, { rejectWithValue, extra: { db }, getState }) => {
  // Narrow by the caller's `clients.read` scope, NOT by the profile role: a head coach is a
  // TEAM_MEMBER with clients.read ALL, and narrowing by `assignedTrainerId` cut campaign and
  // mass-message targeting down to the dozen clients assigned to them personally (TRE-2148,
  // same bug as TRE-2037 in `useTraineeTargeting`). RLS scopes to the tenant and honours the
  // same scope; this only mirrors it.
  const { memberId, isAccountOwner } = getUserClaims(getState());
  // The owner's claims carry ALL, but a legacy session (profile role TRAINER, permissions not
  // yet refreshed) would resolve to NONE and blank the list — keep the explicit escape hatch.
  const scope: PermissionScopeEnum = isAccountOwner
    ? PermissionScope.ALL
    : getPermissionScope(getUserPermissions(getState()), payload?.scopeKey ?? PermissionKey.CLIENTS_READ);

  const trainerIds = await resolveTrainerIdScope(scope, memberId, async (headCoachId) => {
    const { error, data } = await db
      .from("team_member_assigned")
      .select("assignedTrainerId")
      .eq("headCoachId", headCoachId)
      .returns<{ assignedTrainerId: string }[]>();

    return error ? null : data.map((row) => row.assignedTrainerId);
  });

  if (trainerIds?.length === 0) {
    return { list: [], count: 0 };
  }

  try {
    // A tenant-wide list is already over PostgREST's 1000-row cap, which truncates silently —
    // page through it. Paging needs a unique order key, so the list is ordered by `id` here and
    // put back into "newest first" below.
    const list = await fetchAllPostgrestRows<ActiveTraineeListItem>(({ from, to, withCount }) => {
      let query = db
        .from("trainee")
        .select(SELECT_COLUMNS, withCount ? { count: "exact" } : undefined)
        .order("id", { ascending: true })
        .range(from, to)
        .eq("status", TraineeStatus.ACTIVE)
        .not("userId", "is", null);

      if (!payload?.includeLimited) {
        query = query.eq("type", AppAccessType.FullAccess);
      }

      if (trainerIds) {
        query = query.in("assignedTrainerId", trainerIds);
      }

      return query.returns<ActiveTraineeListItem[]>();
    });

    list.sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0));

    return { list, count: list.length };
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : String(error));
  }
});
